import { getOpenAbortModalEvent, getOpenErrorPromptModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useCallback } from 'react'
import { useIntl } from 'react-intl'
import * as R from 'remeda'
import { ActorRefFrom, SnapshotFrom } from 'xstate'

import { secLoansExit } from '../../utils/helpers'
import { useLoanOfferAnalyticsService } from '../analytics/analyticsContext'
import { getAnalyticsNameForPage } from '../analytics/service'
import { FormContextValue } from '../types'
import { LoanOfferWizardData } from './types'
import { createWizardMachine, WizardContext, WizardMachineType } from './wizardMachine'

interface UseWizardLogicReturn extends FormContextValue {
  actor: ActorRefFrom<WizardMachineType>
}

const selectValues = ({
  context: { user, authToken, loanOfferId, loanOffer, wizardData, exitUrls },
  value,
}: SnapshotFrom<WizardMachineType>) => ({
  user,
  authToken,
  loanOfferId,
  loanOffer,
  wizardData,
  step: value,
  exitUrls,
})

export const useWizardLogic = (initialContext: WizardContext): UseWizardLogicReturn => {
  const intl = useIntl()
  const { analyticsService } = useLoanOfferAnalyticsService()
  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine()
  const actor = usePersistingActor<WizardMachineType>(wizardMachine, undefined, undefined, initialContext)
  const send = actor.send

  const { user, authToken, loanOfferId, loanOffer, wizardData, step, exitUrls } = useSelector(
    actor,
    selectValues,
    shallowEqual,
  )

  const openErrorModal = useCallback(
    (requestId?: string) => {
      analyticsService.sendModalViewEvent('ErrorModal')
      modalService.send(
        getOpenErrorPromptModalEvent(
          R.doNothing(),
          intl,
          intl.formatMessage({ id: 'common-file-download-failed-title' }),
          intl.formatMessage({ id: 'common-file-download-failed-description' }),
          ModalVisualMode.TechnicalError,
          requestId,
        ),
      )
    },
    [analyticsService, intl, modalService],
  )

  const goBackToStatus = useCallback(() => {
    analyticsService.sendWizardStepCustomEvent(step, 'GoToStatusLink')
    send({ type: 'BACK_TO_STATUS' })
  }, [send, analyticsService, step])

  const exit = async () => {
    analyticsService.sendCustomEvent(getAnalyticsNameForPage(step), 'ExitApp')
    await secLoansExit({ exitUrls, isSuccess: true })
  }

  return {
    actor,
    user,
    authToken,
    step,
    loanOfferId,
    loanOffer,
    wizardData,
    proceed: useCallback((data?: Partial<LoanOfferWizardData>) => send({ type: 'NEXT_STEP', data }), [send]),
    openErrorModal,
    goBack: useCallback(() => send({ type: 'PREVIOUS_STEP' }), [send]),
    goBackToStatus,
    exit,
    exitWithConfirmDialog: () => {
      modalSend(getOpenAbortModalEvent(() => exit(), intl))
    },
  }
}
